import React, { useState, useEffect } from 'react';
import { useAuth } from '../../firebase/AuthContext';
import { doc, getDoc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaTrophy, FaShippingFast, FaBox } from 'react-icons/fa';
import { sanitizeInput, sanitizePhone } from '../../utils/sanitize';

const ProfileContainer = styled.div`
  max-width: 800px;
  margin: 2rem auto;
  padding: 2rem;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h2`
  color: #333;
  margin-bottom: 1.5rem;
  text-align: center;
`;

const ProfileSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProfileImage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  
  img {
    width: 150px;
    height: 150px;
    border-radius: 50%;
    object-fit: cover;
    margin-bottom: 1rem;
    border: 3px solid #4285f4;
  }
`;

const ProfileInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #555;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #4285f4;
    box-shadow: 0 0 0 2px rgba(66, 133, 244, 0.2);
  }
  
  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  
  @media (max-width: 576px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  background-color: ${props => props.secondary ? 'white' : '#4285f4'};
  color: ${props => props.secondary ? '#4285f4' : 'white'};
  border: ${props => props.secondary ? '1px solid #4285f4' : 'none'};
  border-radius: 4px;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: ${props => props.secondary ? '#f5f5f5' : '#3367d6'};
  }
  
  &:disabled {
    background-color: #b3b3b3;
    border-color: #b3b3b3;
    color: white;
    cursor: not-allowed;
  }
`;

const Message = styled.div`
  padding: 0.75rem;
  margin: 1rem 0;
  border-radius: 4px;
  text-align: center;
  
  &.error {
    background-color: #fdeded;
    color: #d93025;
    border: 1px solid #f8d7da;
  }
  
  &.success {
    background-color: #e6f4ea;
    color: #0f9d58;
    border: 1px solid #d4edda;
  }
`;

const AccountInfo = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #eee;
`;

const AccountInfoTitle = styled.h3`
  color: #333;
  margin-bottom: 1rem;
`;

const InfoItem = styled.div`
  margin-bottom: 1rem;
  
  span {
    font-weight: 500;
    margin-right: 0.5rem;
  }
`;

const AuthMethodBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background-color: ${props => props.provider === 'google' ? '#f5f5f5' : '#e6f4ea'};
  border-radius: 4px;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  
  svg {
    margin-right: 0.5rem;
  }
  
  &.google {
    color: #4285f4;
  }
  
  &.email {
    color: #0f9d58;
  }
`;

// Google logo SVG component
const GoogleLogo = () => (
  <svg width="16" height="16" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
    <path fill="none" d="M0 0h48v48H0z"/>
  </svg>
);

// Email logo SVG component
const EmailLogo = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM20 8L12 13L4 8V6L12 11L20 6V8Z" fill="#0f9d58"/>
  </svg>
);

const UserProfile = () => {
  const { currentUser, updateUserProfile, reauthenticate, updateUserEmail, updateUserPassword, sendVerificationEmail } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    displayName: '',
    email: '',
    phoneNumber: '',
    address: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [userData, setUserData] = useState(null);
  const [clawWinnings, setClawWinnings] = useState([]);
  const [pendingShipments, setPendingShipments] = useState(0);
  const [verificationSent, setVerificationSent] = useState(false);
  
  // Fetch user data from Firestore
  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser?.uid) {
        try {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUserData(data);
            setProfileData(prevData => ({
              ...prevData,
              displayName: currentUser.displayName || '',
              email: currentUser.email || '',
              phoneNumber: data.phoneNumber || '',
              address: data.address || ''
            }));
          }
          
          // Fetch claw winnings
          const winningsSnapshot = await getDocs(
            collection(db, 'users', currentUser.uid, 'treasureHuntPlays')
          );
          const winnings = winningsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          })).sort((a, b) => b.playedAt?.toDate() - a.playedAt?.toDate());
          setClawWinnings(winnings);
          
          // Count pending shipments
          const pending = winnings.filter(w => 
            (w.status === 'pending_shipment' || w.status === 'won') && !w.shippingRequested
          ).length;
          setPendingShipments(pending);
        } catch (error) {
          console.error('Error fetching user data:', error);
          setError('Failed to load user profile data');
        }
      }
    };
    
    fetchUserData();
  }, [currentUser]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('handleEdit called');
    setIsEditing(true);
    setError('');
    setSuccess('');
  };
  
  const handleCancel = () => {
    setIsEditing(false);
    setError('');
    setSuccess('');
    // Reset form data to current user data
    setProfileData(prev => ({
      ...prev,
      displayName: currentUser.displayName || '',
      email: currentUser.email || '',
      phoneNumber: userData?.phoneNumber || '',
      address: userData?.address || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('handleSubmit called, isEditing:', isEditing);
    
    // Don't submit if not in editing mode
    if (!isEditing) {
      console.log('Not in editing mode, preventing submission');
      return;
    }
    
    setError('');
    setSuccess('');
    setLoading(true);
    
    try {
      // Check if user is using Google authentication
      const isGoogleUser = currentUser?.providerData?.[0]?.providerId === 'google.com';
      
      // Update profile in Firebase Auth
      if (profileData.displayName !== currentUser.displayName) {
        await updateUserProfile(currentUser, {
          displayName: profileData.displayName
        });
      }
      
      // Update email if changed and current password provided (only for email/password users)
      if (!isGoogleUser && profileData.email !== currentUser.email && profileData.currentPassword) {
        // Reauthenticate user before changing email
        await reauthenticate(profileData.currentPassword);
        await updateUserEmail(profileData.email);
      }
      
      // Update password if new password provided and current password provided (only for email/password users)
      if (!isGoogleUser && profileData.newPassword && profileData.currentPassword) {
        if (profileData.newPassword !== profileData.confirmPassword) {
          throw new Error('New passwords do not match');
        }
        
        if (profileData.newPassword.length < 6) {
          throw new Error('Password must be at least 6 characters long');
        }
        
        // Reauthenticate user before changing password
        await reauthenticate(profileData.currentPassword);
        await updateUserPassword(profileData.newPassword);
      }
      
      // Update additional user data in Firestore with sanitized inputs
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        displayName: sanitizeInput(profileData.displayName, 100),
        phoneNumber: sanitizePhone(profileData.phoneNumber) || '',
        address: sanitizeInput(profileData.address, 500),
        updatedAt: new Date()
      });
      
      setSuccess('Profile updated successfully!');
      setIsEditing(false);
      
      // Clear sensitive data
      setProfileData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      
    } catch (error) {
      console.error('Error updating profile:', error);
      if (error.code === 'auth/wrong-password') {
        setError('Current password is incorrect');
      } else if (error.code === 'auth/requires-recent-login') {
        setError('Please log out and log back in to update your email or password');
      } else {
        setError(error.message || 'Failed to update profile');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleResendVerification = async () => {
    try {
      setError('');
      setSuccess('');
      setLoading(true);
      await sendVerificationEmail();
      setVerificationSent(true);
      setSuccess('Verification email sent! Please check your inbox.');
    } catch (error) {
      console.error('Error sending verification email:', error);
      setError('Failed to send verification email. Please try again later.');
    } finally {
      setLoading(false);
    }
  };
  
  const defaultProfileImage = 'https://via.placeholder.com/150?text=User';
  const isGoogleUser = currentUser?.providerData?.[0]?.providerId === 'google.com';
  
  return (
    <ProfileContainer>
      <Title>Your Profile</Title>
      
      {error && <Message className="error">{error}</Message>}
      {success && <Message className="success">{success}</Message>}
      
      <ProfileSection>
        <ProfileImage>
          <img 
            src={currentUser?.photoURL || defaultProfileImage} 
            alt={currentUser?.displayName || 'User'} 
          />
          
          <AuthMethodBadge className={isGoogleUser ? 'google' : 'email'}>
            {isGoogleUser ? (
              <>
                <GoogleLogo /> Signed in with Google
              </>
            ) : (
              <>
                <EmailLogo /> Signed in with Email
              </>
            )}
          </AuthMethodBadge>
        </ProfileImage>
        
        <ProfileInfo>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <Label htmlFor="displayName">Name</Label>
              <Input
                type="text"
                id="displayName"
                name="displayName"
                value={profileData.displayName}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="email">Email</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={profileData.email}
                onChange={handleChange}
                disabled={!isEditing || isGoogleUser}
              />
              {isEditing && isGoogleUser && (
                <Message className="info">
                  Email cannot be changed for Google accounts
                </Message>
              )}
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="phoneNumber">Phone Number</Label>
              <Input
                type="tel"
                id="phoneNumber"
                name="phoneNumber"
                value={profileData.phoneNumber}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="address">Address</Label>
              <Input
                type="text"
                id="address"
                name="address"
                value={profileData.address}
                onChange={handleChange}
                disabled={!isEditing}
              />
            </FormGroup>
            
            {isEditing && !isGoogleUser && (
              <>
                <AccountInfoTitle>Change Password</AccountInfoTitle>
                
                <FormGroup>
                  <Label htmlFor="currentPassword">Current Password</Label>
                  <Input
                    type="password"
                    id="currentPassword"
                    name="currentPassword"
                    value={profileData.currentPassword}
                    onChange={handleChange}
                    placeholder="Required to change email or password"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="newPassword">New Password</Label>
                  <Input
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    value={profileData.newPassword}
                    onChange={handleChange}
                    placeholder="Leave blank to keep current password"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="confirmPassword">Confirm New Password</Label>
                  <Input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={profileData.confirmPassword}
                    onChange={handleChange}
                  />
                </FormGroup>
              </>
            )}
            
            <ButtonGroup>
              {isEditing ? (
                <>
                  <Button type="submit" disabled={loading}>
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button type="button" secondary onClick={handleCancel} disabled={loading}>
                    Cancel
                  </Button>
                </>
              ) : (
                <Button type="button" onClick={handleEdit}>
                  Edit Profile
                </Button>
              )}
            </ButtonGroup>
          </Form>
        </ProfileInfo>
      </ProfileSection>
      
      <AccountInfo>
        <AccountInfoTitle>Account Information</AccountInfoTitle>
        <InfoItem>
          <span>Account created:</span>
          {userData?.createdAt?.toDate().toLocaleDateString() || 'N/A'}
        </InfoItem>
        <InfoItem>
          <span>Last login:</span>
          {userData?.lastLogin?.toDate().toLocaleDateString() || 'N/A'}
        </InfoItem>
        <InfoItem>
          <span>Authentication method:</span>
          {isGoogleUser ? 'Google Account' : 'Email and Password'}
        </InfoItem>
        <InfoItem>
          <span>Email verified:</span>
          {currentUser?.emailVerified ? (
            <span style={{ color: '#0f9d58', fontWeight: 'bold' }}>✓ Verified</span>
          ) : (
            <>
              <span style={{ color: '#d93025', fontWeight: 'bold' }}>✗ Not Verified</span>
              {!isGoogleUser && (
                <Button 
                  type="button" 
                  onClick={handleResendVerification} 
                  disabled={loading || verificationSent}
                  style={{ marginLeft: '1rem', padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                >
                  {verificationSent ? 'Email Sent!' : 'Resend Verification Email'}
                </Button>
              )}
            </>
          )}
        </InfoItem>
      </AccountInfo>
      
      {/* Claw Game Winnings Section */}
      <ClawWinningsSection>
        <ClawWinningsHeader>
          <FaTrophy style={{ color: '#D4AF37' }} />
          <AccountInfoTitle style={{ margin: 0 }}>Treasure Hunt Winnings</AccountInfoTitle>
        </ClawWinningsHeader>
        
        {pendingShipments > 0 && (
          <PendingShipmentAlert>
            <FaShippingFast />
            <span>You have <strong>{pendingShipments}</strong> prize{pendingShipments > 1 ? 's' : ''} waiting to be shipped!</span>
            <ShipNowLink to="/treasure-hunt">Request Shipping</ShipNowLink>
          </PendingShipmentAlert>
        )}
        
        {clawWinnings.length > 0 ? (
          <WinningsList>
            {clawWinnings.slice(0, 5).map(winning => (
              <WinningItem key={winning.id}>
                {winning.itemImage ? (
                  <WinningImage src={winning.itemImage} alt={winning.itemName} />
                ) : (
                  <WinningImagePlaceholder><FaBox /></WinningImagePlaceholder>
                )}
                <WinningInfo>
                  <WinningName>{winning.itemName}</WinningName>
                  <WinningDate>
                    {winning.playedAt?.toDate()?.toLocaleDateString() || 'Recently'}
                  </WinningDate>
                </WinningInfo>
                <WinningStatus $status={winning.status} $shipped={winning.shippingRequested}>
                  {winning.status === 'delivered' ? 'Delivered' :
                   winning.status === 'shipped' ? 'Shipped' :
                   winning.shippingRequested ? 'Shipping Requested' : 'Pending'}
                </WinningStatus>
              </WinningItem>
            ))}
          </WinningsList>
        ) : (
          <NoWinningsMessage>
            <FaTrophy style={{ fontSize: '2rem', color: '#ddd', marginBottom: '0.5rem' }} />
            <p>No winnings yet. Try your luck at the Treasure Hunt!</p>
            <PlayNowLink to="/treasure-hunt">Play Now</PlayNowLink>
          </NoWinningsMessage>
        )}
        
        {clawWinnings.length > 5 && (
          <ViewAllLink to="/treasure-hunt">
            View all {clawWinnings.length} winnings →
          </ViewAllLink>
        )}
      </ClawWinningsSection>
    </ProfileContainer>
  );
};

// Additional styled components for claw winnings
const ClawWinningsSection = styled.div`
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid #eee;
`;

const ClawWinningsHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const PendingShipmentAlert = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: linear-gradient(135deg, #fff8e1 0%, #ffecb3 100%);
  border: 1px solid #ffd54f;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  
  svg {
    color: #f57c00;
    font-size: 1.25rem;
  }
  
  span {
    flex: 1;
    color: #5d4037;
  }
`;

const ShipNowLink = styled(Link)`
  padding: 0.5rem 1rem;
  background: #D4AF37;
  color: white;
  text-decoration: none;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  
  &:hover {
    background: #c9a030;
  }
`;

const WinningsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const WinningItem = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  background: #f9f9f9;
  border-radius: 8px;
  
  &:hover {
    background: #f0f0f0;
  }
`;

const WinningImage = styled.img`
  width: 50px;
  height: 50px;
  object-fit: contain;
  border-radius: 6px;
  background: white;
`;

const WinningImagePlaceholder = styled.div`
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #eee;
  border-radius: 6px;
  color: #aaa;
`;

const WinningInfo = styled.div`
  flex: 1;
`;

const WinningName = styled.div`
  font-weight: 500;
  color: #333;
`;

const WinningDate = styled.div`
  font-size: 0.8rem;
  color: #888;
`;

const WinningStatus = styled.div`
  font-size: 0.8rem;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  background: ${props => 
    props.$status === 'delivered' ? '#e8f5e9' :
    props.$status === 'shipped' ? '#e3f2fd' :
    props.$shipped ? '#fff3e0' : '#fce4ec'
  };
  color: ${props => 
    props.$status === 'delivered' ? '#2e7d32' :
    props.$status === 'shipped' ? '#1565c0' :
    props.$shipped ? '#e65100' : '#c62828'
  };
`;

const NoWinningsMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #888;
  
  p {
    margin-bottom: 1rem;
  }
`;

const PlayNowLink = styled(Link)`
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #D4AF37 0%, #F4D03F 100%);
  color: #1a1a2e;
  text-decoration: none;
  border-radius: 25px;
  font-weight: bold;
  
  &:hover {
    transform: scale(1.05);
  }
`;

const ViewAllLink = styled(Link)`
  display: block;
  text-align: center;
  margin-top: 1rem;
  color: #4285f4;
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

export default UserProfile;
